import { RiInformationLine } from 'react-icons/ri';

import { getFrequencyValue } from '@common/calculations';
import { PaymentFrequency, TermAddOn } from '@store/termquote/types';
import { Box, Group, Tooltip, Text } from '@ui';

interface AddOnPremiumRateProps {
  addOn: TermAddOn;
  showFreeAsText?: boolean;
  showInclBaseWithTooltip?: boolean;
  paymentFrequency?: PaymentFrequency;
  compact?: boolean;
}

const AddOnPremiumRate: React.FC<AddOnPremiumRateProps> = ({
  addOn,
  showFreeAsText = false,
  showInclBaseWithTooltip = false,
  paymentFrequency,
  compact = true
}) => {
  // If addon is free render Rs 0 in readable format
  if (addOn.isFree) {
    return (
      <Text
        component="span"
        fz={compact ? 14 : 18}
        fw={compact ? 500 : 600}
        lh={compact ? 1 : 1.4}
        c={showFreeAsText ? 'success.3' : undefined}
      >
        {showFreeAsText ? 'FREE' : `${getFrequencyValue(0, 2)}`}
      </Text>
    );
  }

  const isPremiumZero =
    addOn.additionalPremium && addOn.additionalPremium.base === 0;

  const isConfigurationPending = isPremiumZero && (addOn.limits || addOn.variants);

  if (isConfigurationPending) {
    return (
      <Text component="span" fz={12} c="dimmed">
        {'Select Cover'}
      </Text>
    );
  }

  if (isPremiumZero) {
    return (
      <Text component="span" fz={12} c="dimmed">
        {addOn.isEligible ? 'Not Available' : '--'}
      </Text>
    );
  }

  // If addon has additional premium render formatted rate including tax
  if (addOn.additionalPremium) {
    return (
      <Text
        component="span"
        c={'text'}
        fz={compact ? 14 : 18}
        fw={compact ? 500 : 600}
        lh={compact ? 1 : 1.4}
      >
        {`${getFrequencyValue(
          addOn.additionalPremium.base + addOn.additionalPremium.tax,
          2
        )}`}
        {paymentFrequency && <Text fz={13}>/ {paymentFrequency}</Text>}
      </Text>
    );
  }

  // If addon is neither free and nor has premium breakup - premium is included in base premium
  if (addOn.inclusiveInBasePremium) {
    if (showInclBaseWithTooltip) {
      return (
        <Group
          component="span"
          display="inline-flex"
          align="flex-end"
          fz={compact ? 14 : 18}
          fw={compact ? 500 : 600}
          lh={compact ? 1 : 1.4}
          gap={4}
        >
          Incl. in Base
          <Tooltip label={`Incl. in Base Premium`} position="top" withArrow>
            <RiInformationLine size={16} />
          </Tooltip>
        </Group>
      );
    }

    return <Box component="span">Incl. in Base Premium</Box>;
  }

  // Ideally should never reach here as addon will be free or have additional premium or have its premium included in base premium
  return null;
};

export { AddOnPremiumRate };
